import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationRequestHandler,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  BasicQueryStringUtils,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  LocalStorageBackend,
  LocationLike,
  RedirectRequestHandler,
  TokenRequest,
  TokenRequestHandler,
  TokenResponse
} from '@openid/appauth'
import { serviceApi } from '../service-api'

export interface AuthenticationFlowRedirects {
  authenticationRedirectUri: string
  logoutRedirectUri: string
}

export class AuthenticationFlow {
  private readonly clientId: string
  private readonly oidcUrl: string
  private readonly redirects: AuthenticationFlowRedirects
  private token?: TokenResponse

  private configuration?: AuthorizationServiceConfiguration
  private readonly requestor = new FetchRequestor()
  private readonly requestHandler: AuthorizationRequestHandler
  private readonly tokenHandler: TokenRequestHandler

  constructor(clientId: string, oidcUrl: string, redirects: AuthenticationFlowRedirects) {
    this.clientId = clientId
    this.oidcUrl = oidcUrl
    this.redirects = redirects

    this.requestHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto()
    )

    this.tokenHandler = new BaseTokenRequestHandler(
      this.requestor
    )
  }

  hasToken(): boolean {
    return this.token != null
  }

  authenticated(): boolean {
    return this.token?.isValid() ?? false
  }

  get accessToken(): string | undefined {
    return this.token?.accessToken
  }

  // このメソッドはデバッグ目的で用意されています。
  // リフレッシュトークンが流出した場合、
  // ログイン情報が乗っ取られる危険性があります。
  // 通常のコードでは絶対に使用しないでください。
  get dangerousRefreshToken(): string | undefined {
    return this.token?.refreshToken
  }

  async requestAuthentication(): Promise<void> {
    const request = new AuthorizationRequest({
      client_id: this.clientId,
      redirect_uri: this.redirects.authenticationRedirectUri,
      scope: 'openid profile email address phone xgrab',
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: undefined /* auto generate */,
      extras: undefined
    }, new DefaultCrypto(), true)

    this.requestHandler.performAuthorizationRequest(
      await this.ensureConfiguration(),
      request
    )

    console.log('認証をリクエストしました。')
  }

  async completeAuthentication(): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      function rejectWithLog(reason: Error) {
        console.log(`エラーが発生しました。${reason.message}`)
        reject(reason)
      }

      const notifier = new AuthorizationNotifier()
      notifier.setAuthorizationListener((request, response, _) => {
        if (response == null) {
          rejectWithLog(new Error('Response is null'))
          return
        }

        console.log('認証完了。トークンを要求します。')

        this.requestToken(response.code, request.internal?.code_verifier)
          .then(token => {
            this.token = token
            console.log('トークンが払い出されました。')

            resolve(response.code)
          })
          .catch(rejectWithLog)
      })
      this.requestHandler.setAuthorizationNotifier(notifier)
      this.requestHandler.completeAuthorizationRequestIfPossible()
        .catch(rejectWithLog)

      console.log('認証完了通知を監視します...')
    })
  }

  async refreshAuthenticate(): Promise<void> {
    await this.requestRefreshToken()
    console.log('トークンを更新しました。')
  }

  async getUserInfo<T>(): Promise<T | null> {
    if (!this.authenticated()) return null

    const configuration = await this.ensureConfiguration()
    const response = await serviceApi.get<T>(configuration.userInfoEndpoint ?? '', {
      headers: {
        authorization: `Bearer ${this.token?.accessToken ?? ''}`
      }
    })
    return response.data
  }

  async logout(): Promise<void> {
    if (this.token == null) throw new Error('Token is not found.')

    const configuration = await this.ensureConfiguration()
    if (configuration.endSessionEndpoint == null) {
      throw new Error('ログアウトエンドポイントが設定されていません。')
    }

    // ログアウトページへリダイレクト
    window.location.assign(`${configuration.endSessionEndpoint}?post_logout_redirect_uri=${this.redirects.logoutRedirectUri}&id_token_hint=${this.token?.idToken ?? ''}`)

    // ログアウト時の後処理
    this.token = undefined

    console.log('ログアウトしました。')
  }

  private async ensureConfiguration(): Promise<AuthorizationServiceConfiguration> {
    if (this.configuration == null) {
      this.configuration =
        await AuthorizationServiceConfiguration.fetchFromIssuer(
          this.oidcUrl,
          this.requestor)
    }
    return this.configuration
  }

  private async requestToken(code: string, codeVerifier?: string): Promise<TokenResponse> {
    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.redirects.authenticationRedirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code,
      refresh_token: undefined,
      extras: codeVerifier == null ? {} : { code_verifier: codeVerifier }
    })
    return await this.tokenHandler.performTokenRequest(
      await this.ensureConfiguration(),
      request
    )
  }

  private async requestRefreshToken(): Promise<void> {
    if (this.token == null) throw new Error('Token is not found.')

    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.redirects.authenticationRedirectUri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      code: undefined,
      refresh_token: this.token.refreshToken,
      extras: undefined
    })
    this.token = await this.tokenHandler.performTokenRequest(
      await this.ensureConfiguration(),
      request
    )
  }
}

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse(input: LocationLike, useHash: boolean) {
    return super.parse(input, false /* never use hash */)
  }
}
